import React, { useState } from "react";
import styled from "styled-components/macro";
import Typography from "ui/Typography";
import ConnectCodeForm from "./ConnectCodeForm";
import TeacherPreviewBanner from "./TeacherPreviewBanner";
import useVerifyConnectCode from "./useVerifyConnectCode";
import Button from "ui/Button";
import GuestLayout from "components/GuestLayout/GuestLayout";

import logout from "utils/logout";
import { clearLocalNavState } from "components/Navigation/StudentNavigation/StudentNavigation";
import { useNavigate } from "react-router-dom";
import { AnalyticsProvider } from "utils/analytics/AnalyticsContext";
import { EventName, SurfaceType } from "utils/analytics/analyticsConstants";
import { useCurrentUserNoExcept } from "helpers/useCurrentUser";
import { useConnectStudentMutation } from "./ConnectStudent.generated";
import { assertExists } from "helpers/assertExists";
import NavLink from "ui/router/NavLink";
import TeacherAccount from "ui/Icons/TeacherAccount";
import { IconProps } from "ui/SVGIcon/SVGIcon";
import Inline from "ui/Inline";

const $Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
`;

const $Title = styled(Typography).attrs({
  size: "h2",
  fontWeight: "normal",
})`
  max-width: 37rem;
  margin: auto;
`;

const $Subtitle = styled(Typography).attrs({
  size: "bodySansSerif",
})`
  margin-top: 1.5rem;
`;

const $SignOutWrapper = styled(Typography).attrs({
  size: "bodySansSerif",
})`
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
  gap: 0.2rem;
`;

const $SignOutButton = styled(Button)`
  padding: 0;
`;

const $FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3.5rem;
`;

const $LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
`;

const $TeacherAccount = styled(TeacherAccount)<IconProps>`
  margin: 0 0.25rem 0 0.5rem;
`;

const $SignInLink = styled(NavLink)`
  color: inherit;
  text-decoration: underline;

  &:active,
  &:focus,
  &:hover {
    color: ${({ theme }) => theme.ColorBlurple500};
  }
`;

export default function ConnectCodeSignup() {
  const { loading: loadingConnectCodeVerification, verifyConnectCode } =
    useVerifyConnectCode();

  const [connectStudent, { loading: loadingConnectStudent }] =
    useConnectStudentMutation();

  const loading = loadingConnectCodeVerification || loadingConnectStudent;

  const [error, setError] = useState("");
  const navigate = useNavigate();
  const currentUser = useCurrentUserNoExcept();

  const isNewUser = !currentUser;
  const isTeacher = currentUser?.type === "Teacher";
  const isStudent = currentUser?.type === "Student";

  async function onSubmit(connectCode: string) {
    if (currentUser && !isStudent) {
      return setError("You must be a student to sign up.");
    }

    try {
      if (currentUser) {
        const { data } = await connectStudent({ variables: { connectCode } });

        assertExists(data?.connectStudentWithConnectCode);
        const { student, teacher, isNewConnection } =
          data.connectStudentWithConnectCode;
        // This hard redirect is necessary. Cache updates to the current
        // user don't invoke an update to the top-level student app component,
        // so if the user is archived, the routes won't change, and the user
        // will be stuck in the archived state.
        if (student.teachers.length > 1 && isNewConnection) {
          return (window.location.href = `/?teacher=${teacher.id}`);
        }
        return (window.location.href = "/");
      } else {
        const {
          data: { code: validConnectCode, schoolName },
        } = await verifyConnectCode(connectCode);
        navigate(`/connect/sign-up`, {
          state: {
            from: "/connect",
            event: {
              type: "CONNECT_CODE_STATE",
              connectCode: validConnectCode,
              schoolName,
            },
          },
        });
      }
    } catch (error) {
      setError(error.message);
    }
  }

  function onChange() {
    setError("");
  }

  return (
    <AnalyticsProvider surface={SurfaceType.STUDENT_CONNECT_CODE_SIGNUP_PAGE}>
      {isTeacher && <TeacherPreviewBanner />}
      <GuestLayout userType="student" showSignout={isStudent}>
        <$Header>
          <$Title>
            Enter the class code your teacher shared with you below
          </$Title>
          {isStudent && (
            <>
              <$Subtitle>
                You are currently signed in as <b>{currentUser.email}</b>
              </$Subtitle>
              <$SignOutWrapper>
                Not you?
                <$SignOutButton
                  analyticsName={
                    EventName.StudentConnectCodeSignupPage.LOGGED_OUT
                  }
                  variant="link"
                  onClick={() => {
                    clearLocalNavState();
                    logout();
                  }}
                  requestInProgress={false}
                >
                  Sign out
                </$SignOutButton>
              </$SignOutWrapper>
            </>
          )}
        </$Header>
        <$FormContainer>
          <ConnectCodeForm
            error={error}
            onChange={onChange}
            onSubmit={onSubmit}
            processing={loading}
          />
          {isNewUser && (
            <Inline alignY="center">
              <Typography size="bodySansSerif" fontWeight="bold">
                Already have an account?
              </Typography>
              <$LinkWrapper>
                <$TeacherAccount role="img" size="sm" title="account profile" />
                <$SignInLink
                  to="/"
                  analyticsName={
                    EventName.StudentConnectCodeSignupPage.CLICKED_SIGN_IN
                  }
                >
                  Sign In
                </$SignInLink>
              </$LinkWrapper>
            </Inline>
          )}
        </$FormContainer>
      </GuestLayout>
    </AnalyticsProvider>
  );
}
